import type { EventType, ParticipantRole } from '../types.ts';
import { parseDate } from '../utils/dateUtils.ts';

// Server-issued event IDs zijn UUIDs, lokaal aangemaakte events gebruiken
// Math.random().toString(36). Een simpele UUID-check onderscheidt de twee
// betrouwbaar zonder dat callers expliciet hoeven te tracken welke ids ze
// zelf hebben aangemaakt (#766: localEventIdsRef miste cross-component
// inserts → silent fail bij PUT op niet-bestaande server-id).
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isLocalEventId = (id: string | undefined | null): boolean => {
  if (!id) return false;
  return !UUID_PATTERN.test(id);
};

// --- Bidirectional GEDCOM Tag <-> EventType mapping ---

export const GEDCOM_TAG_TO_EVENT: Record<string, EventType> = {
  // INDI events
  BIRT: 'birth',
  CHR: 'christening',
  DEAT: 'death',
  BURI: 'burial',
  CREM: 'cremation',
  ADOP: 'adoption',
  BAPM: 'baptism',
  BARM: 'bar_mitzvah',
  BASM: 'bas_mitzvah',
  BLES: 'blessing',
  CHRA: 'adult_christening',
  CONF: 'confirmation',
  FCOM: 'first_communion',
  ORDN: 'ordination',
  NATU: 'naturalization',
  EMIG: 'emigration',
  IMMI: 'immigration',
  CENS: 'census',
  PROB: 'probate',
  WILL: 'will',
  GRAD: 'graduation',
  RETI: 'retirement',
  // INDI attributes
  OCCU: 'occupation',
  EDUC: 'education',
  RELI: 'religion',
  TITL: 'title',
  RESI: 'residence',
  PROP: 'property',
  CAST: 'caste',
  DSCR: 'physical_description',
  IDNO: 'national_id',
  NATI: 'nationality',
  // FAM events
  ANUL: 'annulment',
  DIV: 'divorce',
  DIVF: 'divorce_filed',
  ENGA: 'engagement',
  MARB: 'marriage_bann',
  MARC: 'marriage_contract',
  MARL: 'marriage_license',
  MARR: 'marriage',
  MARS: 'marriage_settlement',
  // Generic
  EVEN: 'other',
};

export const EVENT_TO_GEDCOM_TAG: Record<EventType, string> = Object.fromEntries(
  Object.entries(GEDCOM_TAG_TO_EVENT).map(([tag, type]) => [type, tag])
) as Record<EventType, string>;

// Custom types without direct GEDCOM tag → use EVEN with TYPE substructure
EVENT_TO_GEDCOM_TAG['military'] = 'EVEN';
EVENT_TO_GEDCOM_TAG['relocation'] = 'EVEN';
EVENT_TO_GEDCOM_TAG['milestone'] = 'EVEN';
EVENT_TO_GEDCOM_TAG['historical'] = 'EVEN';

/** Tags die naast de standaard-tag óók bij een type horen: doop en kinderdoop lopen door elkaar. */
const GEDCOM_TAG_ALIASES: Partial<Record<string, readonly string[]>> = {
  baptism: ['CHR'],
  christening: ['BAPM'],
};

/**
 * NC #799 — de tag waarmee een event de GEDCOM-export in gaat.
 *
 * De opgeslagen `gedcomTag` won altijd. Werd een beroep later omgezet naar
 * "overig", dan bleef `OCCU` staan en ging het als `1 OCCU` de export in. De
 * opgeslagen tag telt daarom alleen als hij bij het type past: gelijk aan de
 * standaard, een bekende alias (BAPM/CHR), of — bij een type zonder eigen tag
 * (`EVEN` of geen) — een eigen tag die niet van een ánder type is.
 */
export function gedcomExportTag(type: string, opgeslagen?: string | null): string | undefined {
  const standaard: string | undefined = EVENT_TO_GEDCOM_TAG[type as EventType];
  if (!opgeslagen || !standaard) return opgeslagen || standaard;
  const past =
    opgeslagen === standaard ||
    !!GEDCOM_TAG_ALIASES[type]?.includes(opgeslagen) ||
    (standaard === 'EVEN' && !(opgeslagen in GEDCOM_TAG_TO_EVENT));
  return past ? opgeslagen : standaard;
}

/**
 * NC #636 — canonieke `2 TYPE`-waarde voor de vier soorten die als generieke
 * `EVEN` de GEDCOM in gaan.
 *
 * GEDCOM 5.5.1 zegt over TYPE: "a descriptive word or phrase used to further
 * classify the parent event". Het hoort dus de SOORT gebeurtenis te bevatten,
 * niet de specifieke instantie. Pecktree schreef de event-titel in TYPE, zodat
 * een militaire dienst met titel "Reserve" als `2 TYPE Reserve` de export in
 * ging — en daarmee onherkenbaar werd als militaire dienst. Ancestral Author
 * toonde het als losse gebeurtenis "Reserve, 1813" i.p.v. "military service".
 *
 * Engelstalig, want dit is de interoperabiliteitslaag: andere programma's
 * matchen op deze bekende termen. De titel van de gebruiker gaat mee als
 * waarde van de EVEN-regel zelf.
 */
export const EVEN_TYPE_DESCRIPTOR: Partial<Record<EventType, string>> = {
  military: 'Military Service',
  relocation: 'Relocation',
  milestone: 'Milestone',
  historical: 'Historical Event',
};

/**
 * Inverse van EVEN_TYPE_DESCRIPTOR voor de import: herken de descriptor terug
 * naar het Pecktree-event-type. Zonder deze stap komt élke `EVEN` binnen als
 * 'other' en verliest een geëxporteerde militaire dienst zijn soort bij
 * re-import — de roundtrip lekte dus al vóór #636. Nederlandse labels staan
 * erbij omdat gebruikers die zelf kunnen intypen in andere programma's.
 */
const EVEN_DESCRIPTOR_ALIASES: Record<string, EventType> = {
  'military service': 'military',
  military: 'military',
  'militaire dienst': 'military',
  relocation: 'relocation',
  verhuizing: 'relocation',
  milestone: 'milestone',
  mijlpaal: 'milestone',
  'historical event': 'historical',
  historisch: 'historical',
  'historische gebeurtenis': 'historical',
  // #1389 B12 — Geneanet/GeneWeb schrijft woonplaats en kerklidmaatschap als
  // `1 EVEN` met deze descriptors; Pecktree heeft er eigen types voor.
  residence: 'residence',
  woonplaats: 'residence',
  membership: 'religion',
  religion: 'religion',
  religie: 'religion',
};

/** Zoek het event-type bij een `2 TYPE`-descriptor; null als onbekend. */
export function eventTypeFromEvenDescriptor(descriptor: string): EventType | null {
  const key = (descriptor || '').trim().toLowerCase();
  return key ? (EVEN_DESCRIPTOR_ALIASES[key] ?? null) : null;
}

// --- GEDCOM attribute tags die als custom_facts worden geïmporteerd (#619 Fase B) ---
//
// Deze tags worden NIET als events opgeslagen maar als entries in person.facts.
// OCCU, NATI, RESI, RELI blijven events (Pecktree first-class gebeurtenissen).
// FACT is een speciaal geval — de fact-key komt uit de TYPE subtag, niet uit
// deze mapping.

export const GEDCOM_ATTRIBUTE_TAG_TO_FACT_KEY: Record<string, string> = {
  DSCR: 'description',
  CAST: 'caste',
  EDUC: 'education',
  IDNO: 'idNumber',
  NMR: 'numberOfMarriages',
  NCHI: 'numberOfChildren',
  PROP: 'property',
  SSN: 'ssn',
  TITL: 'title',
};

export const FACT_KEY_TO_GEDCOM_ATTRIBUTE_TAG: Record<string, string> = Object.fromEntries(
  Object.entries(GEDCOM_ATTRIBUTE_TAG_TO_FACT_KEY).map(([tag, key]) => [key, tag])
);

// Set voor O(1) lookup bij import — FACT is ook een valid fact-tag maar heeft
// geen vaste fact-key (die komt uit TYPE subtag).
export const GEDCOM_FACT_ATTRIBUTE_TAGS: ReadonlySet<string> = new Set([
  ...Object.keys(GEDCOM_ATTRIBUTE_TAG_TO_FACT_KEY),
  'FACT',
]);

/**
 * GEDCOM 5.5.1 `INDIVIDUAL_ATTRIBUTE_STRUCTURE` — tags die hun waarde op de
 * TAG-REGEL dragen (`1 EDUC Latijnse school`), niet in een `2 TYPE`-subtag.
 *
 * Waarom dit apart staat: deze acht types zijn óók kiesbaar als gebeurtenis in
 * de event-picker. Kiest een gebruiker "Opleiding" met titel "Latijnse school",
 * dan liep dat via de gewone events-lus en kwam het als `1 EDUC` + `2 TYPE …`
 * de export in — leesbaar, maar niet waar een lezer de waarde verwacht. Dat is
 * hetzelfde patroon als NC #635/#636: de waarde op de verkeerde plek, waardoor
 * programma's als Ancestral Author of Geneanet 'm niet tonen.
 *
 * Bewust NIET in deze set:
 * - `OCCU` — heeft al een eigen tak in de export (met description-fallback);
 * - `RESI` — draagt in 5.5.1 géén waarde, alleen EVENT_DETAIL, dus daar is
 *   `2 TYPE` juist de correcte plek.
 */
export const GEDCOM_ATTRIBUTE_TAGS_WITH_VALUE: ReadonlySet<string> = new Set([
  'CAST',
  'DSCR',
  'EDUC',
  'IDNO',
  'NATI',
  'NCHI',
  'NMR',
  'PROP',
  'RELI',
  'SSN',
  'TITL',
]);

/**
 * Event-tags die volgens GEDCOM 5.5.1 de waarde `Y` mogen dragen (#1291).
 *
 * De spec schrijft voor INDIVIDUAL_EVENT_STRUCTURE `n <<EVENT_TAG>> [Y|<NULL>]`.
 * Die `Y` betekent: "de gebeurtenis vond plaats, verdere gegevens ontbreken".
 * Een tag zonder detail én zonder `Y` zegt niets — een lezer mag hem overslaan,
 * en dan verdwijnt de gebeurtenis stil uit het bestand van de genealoog.
 *
 * Bewust NIET in deze set:
 * - de ATTRIBUUT-tags hierboven — die dragen een wáárde (`1 EDUC Latijnse
 *   school`), geen `Y`;
 * - `RESI` — is in 5.5.1 een attribuut, geen event; kent dus geen `Y`;
 * - `EVEN` — draagt de titel als waarde en heeft `2 TYPE` nodig om iets te
 *   betekenen; een `1 EVEN Y` is inhoudsloos.
 */
export const GEDCOM_EVENT_TAGS_ALLOWING_Y: ReadonlySet<string> = new Set([
  'ADOP',
  'BAPM',
  'BARM',
  'BASM',
  'BIRT',
  'BLES',
  'BURI',
  'CENS',
  'CHR',
  'CHRA',
  'CONF',
  'CREM',
  'DEAT',
  'EMIG',
  'FCOM',
  'GRAD',
  'IMMI',
  'NATU',
  'ORDN',
  'PROB',
  'RETI',
  'WILL',
]);

// --- GEDCOM 7.0 ROLE mapping ---

export const GEDCOM_ROLE_TO_PARTICIPANT: Record<string, ParticipantRole> = {
  WITN: 'witness',
  CLERGY: 'clergy',
  OFFICIATOR: 'officiator',
  GODP: 'godparent',
  PARENT: 'primary',
  SPOU: 'primary',
  FRIEND: 'friend',
  NGHBR: 'neighbor',
  OTHER: 'other',
  // Common non-standard values from various GEDCOM exports
  Witness: 'witness',
  Getuige: 'witness',
  Godfather: 'godparent',
  Godmother: 'godparent',
  Peter: 'godparent',
  Meter: 'godparent',
  Informant: 'informant',
  Doctor: 'doctor',
  Notary: 'notary',
  Notaris: 'notary',
};

/**
 * A-29 — `subject` stond hier op 'SUBM'. Dat is in GEDCOM de tag voor de
 * SUBMITTER, de indiener van het bestand, en geen rol binnen een gebeurtenis.
 * De waarde is nu onbereikbaar omdat de export `subject` en `primary` overslaat
 * vóór de `3 RELA`-regel, maar dat is een landmijn: versoepelt die skip ooit,
 * dan gaat élke hoofdpersoon als `3 RELA SUBM` de export in en meldt
 * ontvangende software hem als indiener van het bestand.
 *
 * De hoofdpersoon van een gebeurtenis hóórt geen RELA te krijgen — hij ís de
 * gebeurtenis. Daarom staat er nu een lege string: geen rol om weg te schrijven.
 * De export slaat de rol al over; mocht dat veranderen, dan schrijft hij niets
 * in plaats van iets fouts.
 */
export const PARTICIPANT_TO_GEDCOM_ROLE: Record<ParticipantRole, string> = {
  subject: '',
  partner: 'SPOU',
  primary: 'SPOU',
  witness: 'WITN',
  clergy: 'CLERGY',
  officiator: 'OFFICIATOR',
  godparent: 'GODP',
  informant: 'OTHER',
  doctor: 'OTHER',
  notary: 'OTHER',
  neighbor: 'NGHBR',
  friend: 'FRIEND',
  other: 'OTHER',
};

// --- Event type categories (for UI grouping) ---

export type EventCategory =
  'personal' | 'religious' | 'legal' | 'migration' | 'family' | 'education' | 'attribute' | 'other';

export const EVENT_CATEGORIES: Record<EventCategory, EventType[]> = {
  personal: ['birth', 'death', 'burial', 'cremation', 'adoption', 'retirement'],
  religious: [
    'baptism',
    'christening',
    'adult_christening',
    'confirmation',
    'first_communion',
    'ordination',
    'bar_mitzvah',
    'bas_mitzvah',
    'blessing',
  ],
  legal: ['naturalization', 'probate', 'will', 'census', 'national_id'],
  migration: ['emigration', 'immigration', 'residence', 'relocation'],
  family: [
    'marriage',
    'divorce',
    'divorce_filed',
    'engagement',
    'annulment',
    'marriage_bann',
    'marriage_contract',
    'marriage_license',
    'marriage_settlement',
    'family_other',
    // NOTE: residence was here; moved to migration-only to prevent dropdown duplication (issue #678)
  ],
  education: ['education', 'graduation'],
  attribute: [
    'occupation',
    'religion',
    'title',
    'nationality',
    'property',
    'caste',
    'physical_description',
    'military',
  ],
  other: ['milestone', 'historical', 'other'],
};

export function getEventCategory(type: EventType): EventCategory {
  for (const [cat, types] of Object.entries(EVENT_CATEGORIES)) {
    if (types.includes(type)) return cat as EventCategory;
  }
  return 'other';
}

// --- Vital events that sync to person columns ---

export const VITAL_EVENT_TYPES: EventType[] = ['birth', 'death', 'baptism', 'burial'];

export function isVitalEvent(type: EventType): boolean {
  return VITAL_EVENT_TYPES.includes(type);
}

export function isFamilyEvent(type: EventType): boolean {
  return EVENT_CATEGORIES.family.includes(type);
}

// Events die per definitie twee personen vereisen (huwelijk, scheiding, verloving).
// Onderscheiden van isFamilyEvent: residence/family_other zitten wel in de family
// categorie (kunnen gedeeld zijn) maar zijn niet bilateraal vereist.
const BILATERAL_EVENT_TYPES: ReadonlySet<EventType> = new Set<EventType>([
  'marriage',
  'divorce',
  'divorce_filed',
  'engagement',
  'annulment',
  'marriage_bann',
  'marriage_contract',
  'marriage_license',
  'marriage_settlement',
]);

export function isBilateralEvent(type: EventType): boolean {
  return BILATERAL_EVENT_TYPES.has(type);
}

// Event-types waar `title` de concrete waarde is die de gebruiker invult
// (bijv. beroep = 'Naaister', titel = 'Baron'). Voor deze types moet de
// EventModal een label-input tonen zodat de waarde bewerkbaar is, en toont
// DetailPanel/EventRow de weergave als "Label — waarde" (bijv "Beroep — Naaister").
const TITLED_ATTRIBUTE_TYPES: ReadonlySet<EventType> = new Set<EventType>([
  // Attribute/metadata events — title = concrete waarde
  'occupation',
  'education',
  'title',
  'property',
  'caste',
  'national_id',
  'nationality',
  'graduation',
  'military',
  'religion', // Nextcloud #552 — religienaam (bijv. "Nederlands Hervormd")
  // Custom/generic events — title = door user gekozen label
  'milestone',
  'historical',
  'other',
  'family_other',
  'relocation',
]);

export function hasTitleValue(type: EventType): boolean {
  return TITLED_ATTRIBUTE_TYPES.has(type);
}

// --- Date sort year extraction ---

export function extractSortYear(dateStr?: string): number | null {
  if (!dateStr) return null;
  const match = dateStr.match(/(\d{4})/);
  return match ? parseInt(match[1]) : null;
}

// --- GenealogyEvent UI helpers (#701 — vervangen de legacy LifeEvent UI-only velden) ---

/**
 * Afgeleide "year string" voor display (timeline year-markers, fallback-sort).
 * Prefereer `dateSortYear` als dat gezet is, anders eerste 4 digits uit date-string.
 */
export function getEventYear(event: { date?: string; dateSortYear?: number }): string {
  if (event.dateSortYear != null) return String(event.dateSortYear);
  return event.date?.match(/\d{4}/)?.[0] || '';
}

/**
 * Platte `sourceIds` uit de `sources` array — voor legacy consumers die een
 * plain array verwachten zonder citation-metadata (page/quality/notes).
 */
export function getEventSourceIds(event: { sources?: Array<{ sourceId: string }> }): string[] {
  return (event.sources || []).map((s) => s.sourceId);
}

/**
 * Eerste source-ID als het event één of meer bronnen heeft.
 * Handig voor UI-snippets die alleen "heeft bron?" tonen.
 */
export function getEventPrimarySourceId(event: {
  sources?: Array<{ sourceId: string }>;
}): string | null {
  return event.sources && event.sources.length > 0 ? event.sources[0].sourceId : null;
}

// --- Effectieve event-volgorde (#688) ---

type SortableEvent = {
  sortOrder?: number | null;
  year?: string;
  fullDate?: string;
  date?: string;
  dateSortYear?: number;
  type?: string;
};

/**
 * A-19 — vangnet voor gebeurtenissen die alleen een jaartal hebben. Het merendeel
 * van de genealogische data is jaar-only: bij een kind dat in 1814 geboren wordt,
 * gedoopt wordt en datzelfde jaar overlijdt zijn alle vier de sleutels gelijk en
 * besliste tot nu toe de aanmaakvolgorde. Een levensfase-volgorde is dan het enige
 * eerlijke antwoord: geboren gaat vooraf aan gedoopt, begraven komt na overleden.
 *
 * Alleen als tie-breaker gebruikt — een echte datum wint altijd van deze rangorde.
 */
const LEVENSFASE_RANG: Record<string, number> = {
  birth: 0,
  baptism: 1,
  death: 8,
  burial: 9,
};

function levensfaseRang(type?: string): number {
  return type ? (LEVENSFASE_RANG[type] ?? 5) : 5;
}

/**
 * Chronologische sleutel — JJJJMMDD, zodat gebeurtenissen binnen hetzelfde jaar
 * ook op volgorde staan. Zonder datum onderaan.
 *
 * B-20 — dit was uitsluitend het jaartal. Twee gebeurtenissen in hetzelfde jaar
 * stonden daardoor in de volgorde waarin ze toevallig uit de database kwamen: een
 * doop van 8 januari kon bóven de geboorte van 3 januari belanden. Voor een tijdlijn
 * is dat precies het verkeerde soort fout — de genealoog leest er een volgorde in
 * die er niet is.
 *
 * Ontbrekende maand of dag telt als 0, dus "1848" komt vóór "3 JAN 1848". Dat is de
 * juiste kant op: een vage datum hoort vóór de precieze datums van datzelfde jaar,
 * niet ertussendoor op een willekeurige plek.
 *
 * GenealogyEvent: dateSortYear (number) + date (string); LifeEvent (legacy): year +
 * fullDate.
 */
function chronoSortKey(e: SortableEvent): number {
  const raw = e.date || e.fullDate || e.year;
  const parsed = typeof raw === 'string' ? parseDate(raw) : null;
  const jaar = parsed?.year ?? e.dateSortYear ?? extractSortYear(raw);
  // MAX_SAFE_INTEGER en niet Infinity: twee ongedateerde events gaven anders
  // Infinity - Infinity = NaN, en een NaN-comparator maakt de sortering ongeldig.
  if (jaar == null) return Number.MAX_SAFE_INTEGER;
  return jaar * 10000 + (parsed?.month ?? 0) * 100 + (parsed?.day ?? 0);
}

// #588 — sortOrder (kleine ints zoals 100/200) en het jaar (4-cijferig) leven in
// verschillende numerieke ruimtes. Ze samen vergelijken liet een huwelijk
// (sortOrder=100) vóór een geboorte (jaar 1814) belanden. Honoreer sortOrder daarom
// alleen in ECHTE handmatige modus — wanneer ÉLK event er één heeft (zo zet de
// reorder ze ook altijd; vitale events uit migratie 146 hebben er geen). In alle
// andere (mixed) gevallen sorteren we puur chronologisch. Backend doet hetzelfde.
export function sortEventsByEffectiveOrder<T extends SortableEvent>(events: readonly T[]): T[] {
  const chrono = (a: T, b: T) =>
    chronoSortKey(a) - chronoSortKey(b) || levensfaseRang(a.type) - levensfaseRang(b.type);
  const manual = events.filter((e) => e.sortOrder != null);
  if (manual.length === 0) return [...events].sort(chrono);

  // NC #663 / A-17 — handmatig zodra minstens één event een volgorde heeft.
  // Voorheen klapte één event zónder sortOrder (een nieuw event, een relatie-
  // bewerking, een synthetische geboorte-rij in het dossier) de hele persoon
  // terug naar chronologisch, en leek "opslaan" niets te doen. De events met
  // volgorde houden hun volgorde; events zonder volgorde schuiven op hun
  // chronologische positie ertussen (achter het laatste event dat niet later
  // valt). sortOrder en jaar worden nooit met elkaar vergeleken (#588).
  const vast = [...manual].sort((a, b) => (a.sortOrder as number) - (b.sortOrder as number));
  // Effectieve plek van elk vast event: zijn datum (+ levensfase als tiebreak);
  // een ongedateerd vast event erft de plek van zijn voorganger, want daar
  // heeft de gebruiker het neergezet.
  const eff: { key: number; rank: number }[] = [];
  vast.forEach((m, i) => {
    const key = chronoSortKey(m);
    eff[i] =
      key !== Number.MAX_SAFE_INTEGER
        ? { key, rank: levensfaseRang(m.type) }
        : (eff[i - 1] ?? { key: -1, rank: -1 });
  });
  const loose = events.filter((e) => e.sortOrder == null).sort(chrono);
  const nietLater = (i: number, e: T) => {
    const key = chronoSortKey(e);
    const rank = levensfaseRang(e.type);
    return eff[i].key < key || (eff[i].key === key && eff[i].rank <= rank);
  };
  // Invoegpositie per los event = aantal vaste events dat niet later valt.
  const plek = loose.map((e) => ({ e, at: eff.filter((_, i) => nietLater(i, e)).length }));
  const result: T[] = [];
  for (let i = 0; i <= vast.length; i++) {
    for (const { e, at } of plek) if (at === i) result.push(e);
    if (i < vast.length) result.push(vast[i]);
  }
  return result;
}

/**
 * Staat deze reeks in handmatige modus?
 *
 * Dezelfde vraag als `sortEventsByEffectiveOrder` hierboven stelt: is er
 * minstens één event met een volgorde? (NC #663 / #1386.) Met `.every` (A-18)
 * dwong de sortering de handmatige plekken af terwijl de editor geen
 * "handmatig"-vlag toonde: geen ↻-knop, dus geen weg terug, en jaarkoppen die
 * de "ongedateerd"-scheiding midden in de tijdlijn zetten.
 *
 * A-18 zelf is aan de bron opgelost: de relatie-route geeft huwelijk en
 * scheiding geen `sort_order` 0/1 meer (routes/relationships.js), dus een
 * volgorde bestaat alleen nog als de gebruiker hem zelf zette.
 */
export function hasManualOrder(events: readonly SortableEvent[]): boolean {
  return events.some((e) => e.sortOrder != null);
}

// --- Convert legacy LifeEvent type to new EventType ---

export function legacyTypeToEventType(legacyType: string): EventType {
  const mapping: Record<string, EventType> = {
    birth: 'birth',
    marriage: 'marriage',
    death: 'death',
    baptism: 'baptism',
    burial: 'burial',
    emigration: 'emigration',
    immigration: 'immigration',
    military: 'military',
    education: 'education',
    divorce: 'divorce',
    adoption: 'adoption',
    naturalization: 'naturalization',
    religion: 'religion',
    retirement: 'retirement',
    relocation: 'relocation',
    testament: 'will',
    other: 'other',
    milestone: 'milestone',
    historical: 'historical',
    occupation: 'occupation',
  };
  return mapping[legacyType] || 'other';
}
